"use client";

import { useCallback, useEffect, useState } from "react";

/**
 * Shared activation hook for interaction-deferred tool islands (the proven
 * `DeferredKundaliFormCard` pattern, generalized for the calculator surfaces).
 *
 * The heavy interactive chunk (BirthDetailsForm + PlaceAutocomplete + result
 * stack) stays OUT of the route's initial JS: the route renders a
 * height-reserving form shell (rule 58 — never a 0px collapse; rule 43/61 —
 * the tool shell still owns the first viewport), and the real tool chunk
 * loads on first interaction or after `deferMs`, whichever comes first.
 */
const FORM_EVENTS = ["pointerdown", "keydown", "touchstart", "focusin"] as const;

const DEFAULT_DEFER_MS = 3600;

export function useDeferredToolActivation({
  deferMs = DEFAULT_DEFER_MS,
  interactionOnly = false,
}: {
  deferMs?: number;
  interactionOnly?: boolean;
} = {}): { ready: boolean; activate: () => void } {
  const [ready, setReady] = useState(false);
  const activate = useCallback(() => setReady(true), []);

  useEffect(() => {
    if (ready) return;

    const timer = interactionOnly ? null : window.setTimeout(activate, deferMs);
    const options: AddEventListenerOptions = { once: true, passive: true };
    for (const eventName of FORM_EVENTS) {
      window.addEventListener(eventName, activate, options);
    }

    return () => {
      if (timer !== null) window.clearTimeout(timer);
      for (const eventName of FORM_EVENTS) {
        window.removeEventListener(eventName, activate);
      }
    };
  }, [activate, deferMs, interactionOnly, ready]);

  return { ready, activate };
}
